import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";
import FinanceContainer from "../containers/FinanceContainer";
import { useRouter } from "../../../hooks/useRouter";
import config from "../../../config";
import { formatCurrency } from "../../../utils";

interface CreditorInvoice {
  id: string;
  invoiceNo: string;
  invoiceDate: string;
  dueDate?: string;
  description?: string;
  amountIncl: number;
  amountPaid?: number;
  status: string;
}

interface CreditorPayment {
  id: string;
  paymentNo?: string;
  paymentDate: string;
  amount: number;
  paymentMethod?: string;
  reference?: string;
  notes?: string;
}

interface CreditorFullData {
  id: string;
  fullName: string;
  email?: string;
  contactPhone?: string;
  contactPerson?: string;
  developmentName?: string;
  address?: string;
  bankName?: string;
  bankAccountNumber?: string;
  paymentTerms?: string;
  notes?: string;
  active?: boolean;
  invoices: CreditorInvoice[];
  payments: CreditorPayment[];
  summary: {
    totalInvoiced: number;
    totalPaid: number;
    balanceDue: number;
  };
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "-";

const CreditorDetail: React.FC = () => {
  const router = useRouter();
  const { id } = router.params as { id?: string };
  const [creditor, setCreditor] = useState<CreditorFullData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditor = useCallback(async () => {
    if (!id) {
      setError("Missing creditor ID");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/creditors/${id}/full`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to load creditor profile");
      }

      const data = await response.json();
      setCreditor(data);
    } catch (err: any) {
      setError(err.message || "Failed to load creditor profile");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCreditor();
  }, [fetchCreditor]);

  const summaryCards = useMemo(
    () =>
      creditor
        ? [
            { label: "Total Invoiced", value: formatCurrency(creditor.summary.totalInvoiced) },
            { label: "Total Paid", value: formatCurrency(creditor.summary.totalPaid) },
            { label: "Balance Due", value: formatCurrency(creditor.summary.balanceDue) },
          ]
        : [],
    [creditor],
  );

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton onClick={() => router.push("/finance/creditors")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Creditor Profile
            </Typography>
            <Typography color="text.secondary">
              Supplier balances, invoices, and payment history.
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : creditor ? (
          <>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h5" fontWeight={700}>
                      {creditor.fullName}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                      <Chip
                        icon={<BusinessIcon />}
                        label={creditor.developmentName || "No development"}
                        variant="outlined"
                      />
                      <Chip
                        label={creditor.active ? "Active" : "Inactive"}
                        color={creditor.active ? "success" : "default"}
                      />
                    </Box>
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      Contact person: {creditor.contactPerson || "-"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      Phone: {creditor.contactPhone || "-"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      Email: {creditor.email || "-"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      Payment terms: {creditor.paymentTerms || "-"}
                    </Typography>
                    {creditor.notes ? (
                      <Typography sx={{ mt: 2 }}>{creditor.notes}</Typography>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#f8fbff",
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Bank
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {creditor.bankName || "-"}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Account Number
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {creditor.bankAccountNumber || "-"}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {summaryCards.map((item) => (
                <Grid item xs={12} sm={4} key={item.label}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
                        {item.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} lg={7}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Invoices
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                            <TableCell sx={{ fontWeight: 700 }}>Invoice</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Due</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Amount
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Paid
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {creditor.invoices.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No invoices found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            creditor.invoices.map((invoice) => (
                              <TableRow key={invoice.id}>
                                <TableCell>
                                  <Typography fontWeight={600}>
                                    {invoice.invoiceNo}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {invoice.description || "No description"}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                                <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                <TableCell>
                                  <Chip label={invoice.status} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(invoice.amountIncl)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(invoice.amountPaid || 0)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Payments
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                            <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {creditor.payments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                No payments found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            creditor.payments.map((payment) => (
                              <TableRow key={payment.id}>
                                <TableCell>
                                  <Typography fontWeight={600}>
                                    {payment.paymentNo || payment.reference || "-"}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {payment.notes || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                                <TableCell>{payment.paymentMethod || "-"}</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(payment.amount)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {creditor.email ? (
                      <Chip
                        icon={<EmailIcon />}
                        label={creditor.email}
                        variant="outlined"
                        sx={{ mt: 2, mr: 1 }}
                      />
                    ) : null}
                    {creditor.paymentTerms ? (
                      <Chip
                        icon={<PaymentsIcon />}
                        label={creditor.paymentTerms}
                        variant="outlined"
                        sx={{ mt: 2 }}
                      />
                    ) : null}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : null}
      </Box>
    </FinanceContainer>
  );
};

export default CreditorDetail;
